import React, { useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useNotifications } from '../hooks/useNotifications';

const NotificationDropdown = ({ isOpen, onClose, isEmployer = false }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dropdownRef = useRef(null);
    const { notifications, unreadMessages, unreadCount, markAsRead, deleteNotification } = useNotifications();

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    const formatTimeAgo = (timestamp) => {
        const now = new Date();
        const messageTime = new Date(timestamp);
        const diffInSeconds = Math.floor((now - messageTime) / 1000);

        if (diffInSeconds < 60) return 'just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
        return `${Math.floor(diffInSeconds / 86400)}d ago`;
    };

    const truncateMessage = (content, maxLength = 50) => {
        if (content === null || content === undefined) return '';

        let baseText = '';
        if (typeof content === 'string') {
            baseText = content;
        } else if (typeof content === 'object') {
            try {
                baseText = JSON.stringify(content);
            } catch {
                baseText = String(content);
            }
        } else {
            baseText = String(content);
        }

        // Strip HTML tags for preview
        const textContent = baseText.replace(/<[^>]+>/g, '').trim();
        if (textContent.length <= maxLength) return textContent;
        return textContent.substring(0, maxLength) + '...';
    };

    const handleNotificationClick = (notification) => {
        markAsRead(notification.id);
        onClose();
        
        if (notification.type === 'message') {
            // Navigate to messages page with conversation
            const path = location.pathname.startsWith('/employee') 
                ? '/employee/messages' 
                : '/employer/messages';
            
            navigate(path, { 
                state: { openConversationId: notification.data?.conversation_id } 
            });
        } else if (notification.type === 'application') {
            navigate(`/employer/candidates?jobId=${notification.data?.job_id}`);
        }
    };

    // Transform notifications to display format
    const allMessages = notifications
        .filter(n => !n.read)
        .map(n => ({
            ...n,
            notificationType: n.type,
            id: n.id,
            conversation_id: n.data?.conversation_id,
            sender_name: n.data?.sender_name || n.message?.split(' applied')[0] || 'Someone',
            content: n.data?.content || n.message,
            created_at: n.created_at,
            timestamp: n.created_at
        }))
        .sort((a, b) => {
            const timeA = new Date(a.created_at).getTime();
            const timeB = new Date(b.created_at).getTime();
            return timeB - timeA;
        });

    if (!isOpen) return null;

    return (
        <div 
            ref={dropdownRef}
            className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-[9999] max-h-96 overflow-y-auto"
        >
            <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-900">Messages</h3>
                    {unreadCount > 0 && (
                        <button
                            onClick={() => {
                                markAsRead();
                                onClose();
                            }}
                            className="text-xs text-gray-900 hover:text-gray-700"
                        >
                            Mark all as read
                        </button>
                    )}
                </div>
            </div>

            <div className="py-2">
                {allMessages.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-gray-500">
                        No new messages
                    </div>
                ) : (
                    allMessages.map((item) => {
                        const isApplication = item.notificationType === 'application';
                        
                        return (
                            <div
                                key={item.id}
                                className={`px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                                    !item.read && isApplication ? 'bg-blue-50' : ''
                                }`}
                                onClick={() => handleNotificationClick(item)}
                            >
                                <div className="flex items-start justify-between mb-1">
                                    <div className="flex items-center gap-2">
                                        {isApplication && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                New Application
                                            </span>
                                        )}
                                        <span className="text-sm font-medium text-gray-900">
                                            {item.sender_name}
                                        </span>
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                deleteNotification(item.id);
                                            }}
                                            className="text-gray-400 hover:text-gray-600"
                                            title="Delete"
                                        >
                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                        <span className="text-xs text-gray-400">
                                            {formatTimeAgo(item.created_at || item.timestamp)}
                                        </span>
                                    </div>
                                </div>
                                <p className="text-sm text-gray-600 line-clamp-2">
                                    {truncateMessage(item.content || item.message, 60)}
                                </p>
                            </div>
                        );
                    })
                )}
            </div>

            {allMessages.length > 0 && (
                <div className="p-3 border-t border-gray-200">
                    <button
                        onClick={() => {
                            const path = location.pathname.startsWith('/employee') 
                                ? '/employee/messages' 
                                : '/employer/messages';
                            navigate(path);
                            onClose();
                        }}
                        className="w-full text-center text-sm text-gray-900 hover:text-gray-700 font-medium"
                    >
                        View all messages
                    </button>
                </div>
            )}
        </div>
    );
};

export default NotificationDropdown;

